<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    :style="{ width: '40rem' }"
    :show-close-icon="false"
    :pt="{ content: { class: 'px-6' } }"
    @hide="handleClose"
  >
    <template #header>
      <div class="w-full flex justify-between items-center">
        <h2 class="text-xl font-semibold">Add new payment method</h2>
        <button
          class="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
          @click="isVisible = false"
        >
          <i class="pi pi-times text-xl" />
        </button>
      </div>
    </template>

    <div class="space-y-6">
      <PayabliPaymentForm
        :api-token="apiToken"
        :entry-point-name="entryPointName"
        :amount="amount"
        :payment-option="paymentOption"
        :customer-number="customerNumber"
        :is-processing-payment="isProcessingPayment"
        :make-payment="makePayment"
      />
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import Drawer from "primevue/drawer";
import PayabliPaymentForm from "./PayabliPaymentForm.vue";

interface Props {
  visible: boolean;
  amount: number;
  paymentFrequency?: string;
  paymentOption: "one-time" | "installments";
  apiToken: string;
  entryPointName: string;
  customerNumber: string;
}

const props = withDefaults(defineProps<Props>(), {
  paymentFrequency: undefined,
});

const emit = defineEmits<{
  "update:visible": [value: boolean];
  success: [paymentReference: string];
  cancel: [];
}>();

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

// Payment processing state
const isProcessingPayment = ref(false);

// Payment processing callback - called by Payabli when payment method is created
const makePayment = async (
  referenceType: string,
  referenceId: string,
  paymentOption: string
): Promise<boolean> => {
  isProcessingPayment.value = true;

  try {
    // Simulate API call - in real implementation, this would save the payment method
    await new Promise((resolve) => setTimeout(resolve, 2000));

    emit("success", referenceId);
    isVisible.value = false;

    return true;
  } catch (error) {
    console.error("Payment processing failed:", error);
    return false;
  } finally {
    isProcessingPayment.value = false;
  }
};

// Format amount for display
const formattedAmount = computed(() => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(props.amount);
});

const handleClose = () => {
  emit("cancel");
};
</script>
